import { useEffect, useRef, type RefObject } from 'react';

// Invoke handler on Escape while `active`.
export function useOnEscape(active: boolean, handler: () => void): void {
  useEffect(() => {
    if (!active) {
      return;
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        handler();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [active, handler]);
}

// Invoke handler on pointerdown outside the referenced element while `active`.
export function useOnClickOutside(
  ref: RefObject<HTMLElement | null>,
  active: boolean,
  handler: () => void,
): void {
  useEffect(() => {
    if (!active) {
      return;
    }
    const onDown = (e: MouseEvent): void => {
      const el = ref.current;
      if (el !== null && e.target instanceof Node && !el.contains(e.target)) {
        handler();
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [ref, active, handler]);
}

// Lock body scroll while `active` (for modal/drawer overlays).
export function useBodyScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) {
      return;
    }
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [active]);
}

export interface FocusTrapOptions {
  /** Explicit initial-focus target; optional and backward-compatible for existing callers. */
  readonly initialFocusRef?: RefObject<HTMLElement | null> | undefined;
  /** Escape handler; fires only while this layer is topmost, decided at event time. */
  readonly onEscape?: (() => void) | undefined;
}

const TABBABLE_SELECTOR =
  'a[href]:not([aria-disabled="true"]), button:not([disabled]):not([aria-disabled="true"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function getTabbable(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR));
}

// Whether `el` can still receive programmatic focus: connected, enabled, and not buried in a
// hidden/inert subtree. Used to decide whether a captured opener is safe to restore focus to.
function canReceiveFocus(el: HTMLElement | null): el is HTMLElement {
  if (el === null || !el.isConnected) {
    return false;
  }
  for (let node: HTMLElement | null = el; node !== null; node = node.parentElement) {
    if (
      (node as Partial<HTMLButtonElement>).disabled === true ||
      node.getAttribute('aria-disabled') === 'true' ||
      node.hidden ||
      node.hasAttribute('inert') ||
      node.style.display === 'none' ||
      node.style.visibility === 'hidden'
    ) {
      return false;
    }
  }
  return true;
}

interface Layer {
  readonly id: symbol;
  readonly container: HTMLElement;
}

// Module-level stack of active overlay layers, ordered oldest-to-newest. Only the topmost
// layer's Tab/Escape handler acts, so a Modal opened from within an open Drawer becomes the sole
// trap owner; the Drawer's handler resumes once the Modal's cleanup removes it from the stack.
let activeLayers: Layer[] = [];

// Shared focus lifecycle for layered overlays (Modal/Drawer): captures the element focused
// immediately before opening, moves focus into the panel, traps Tab/Shift+Tab and routes Escape
// to the topmost active layer, and restores the captured opener on close/unmount — falling back
// to the underlying layer when the opener can no longer receive focus.
export function useFocusTrap(
  active: boolean,
  containerRef: RefObject<HTMLElement | null>,
  options: FocusTrapOptions = {},
): void {
  const { initialFocusRef, onEscape } = options;
  const layerId = useRef(Symbol('focus-layer')).current;
  const openerRef = useRef<HTMLElement | null>(null);
  // Keep the latest Escape handler in a ref so the keydown listener never re-subscribes and
  // always calls the current callback rather than one captured at effect-setup time.
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;

  useEffect(() => {
    if (!active) {
      return;
    }
    const container = containerRef.current;
    if (container === null) {
      return;
    }

    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    activeLayers = [...activeLayers, { id: layerId, container }];

    const explicit = initialFocusRef?.current ?? null;
    const initial = explicit ?? getTabbable(container)[0] ?? container;
    initial.focus();

    return () => {
      activeLayers = activeLayers.filter((l) => l.id !== layerId);
      const opener = openerRef.current;
      if (canReceiveFocus(opener)) {
        opener.focus();
        return;
      }
      // Opener is gone/unfocusable: hand focus to whatever layer is now topmost rather than let
      // it fall to <body>, so an underlying trap keeps a valid focus anchor.
      const top = activeLayers[activeLayers.length - 1];
      if (top !== undefined) {
        (getTabbable(top.container)[0] ?? top.container).focus();
      }
    };
  }, [active, containerRef, initialFocusRef, layerId]);

  useEffect(() => {
    if (!active) {
      return;
    }
    const onKeyDown = (e: KeyboardEvent): void => {
      // Ownership is resolved against the live stack on each event, never a stale render value.
      const isTopmost = activeLayers[activeLayers.length - 1]?.id === layerId;
      if (e.key === 'Escape') {
        if (isTopmost) {
          onEscapeRef.current?.();
        }
        return;
      }
      if (e.key !== 'Tab' || !isTopmost) {
        return;
      }
      const container = containerRef.current;
      if (container === null) {
        return;
      }
      const tabbable = getTabbable(container);
      if (tabbable.length === 0) {
        e.preventDefault();
        container.focus();
        return;
      }
      // Manually drive the whole tab order (rather than only intercepting at the edges) so the
      // trap is deterministic regardless of environment-specific native Tab behavior.
      const delta = e.shiftKey ? -1 : 1;
      const currentIndex = tabbable.indexOf(document.activeElement as HTMLElement);
      const nextIndex =
        currentIndex === -1
          ? (e.shiftKey ? tabbable.length - 1 : 0)
          : (currentIndex + delta + tabbable.length) % tabbable.length;
      const next = tabbable[nextIndex];
      if (next !== undefined) {
        e.preventDefault();
        next.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [active, containerRef, layerId]);
}
